/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import Notify from "@eisinc/App/Components/Notify";
import { PaymentOnAccountModel } from "@eisinc/App/Components/PaymentCenter/PaymentOnAccount";
import PDFLinkCell from "@eisinc/App/Components/PaymentCenter/PDFLinkCell";
import { calcSelectedInvoicesTotal, formatter } from "@eisinc/App/Utilities/PaymentCenterUtilities";
import { InvoicePaymentModel } from "@eisinc/XDL/interfaces";
import { color } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    selectedInvoicePayments?: InvoicePaymentModel[] | undefined;
    paymentOnAccountData?: PaymentOnAccountModel;
    currentStep: number;
    setCurrentStep: (i: number) => void;
    selectedCredits?: InvoicePaymentModel[] | undefined;
}

const mapDispatchToProps = {};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface PaymentCenterSummaryStyles {
    wrapper?: InjectableCss;
    summaryLabel?: TypographyProps;
    totalWrapper?: InjectableCss;
    leftGridItem?: GridItemProps;
    rightGridItem?: GridItemProps;
    proceedButton?: ButtonPresentationProps;
    invoiceNumber?: TypographyProps;
    gridContainer?: GridContainerProps;
    listWrapper?: InjectableCss;
    actionWrapper?: GridItemProps;
    errorMessage?: InjectableCss;
    invoiceItemNumberDataCellLink?: LinkPresentationProps;
    spinner?: LoadingSpinnerProps;
    invoiceItemNumberNonCredit?: TypographyPresentationProps;
}

export const styles: PaymentCenterSummaryStyles = {
    wrapper: {
        css: css`
            border: 1px solid ${color("common.ui4")};
            padding: 12px;
            width: 100%;
        `,
    },
    summaryLabel: {
        lineHeight: "29px",
        weight: "bold",
        css: css`
            border-bottom: 1px solid ${color("common.ui4")};
            padding-bottom: 2px;
        `,
    },
    totalWrapper: {
        css: css`
            border-top: 1px solid ${color("common.ui4")};
            margin-top: 12px;
            padding-top: 12px;
        `,
    },
    leftGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            ${TypographyStyle} {
                font-size: 16px;
                line-height: 19px;
            }
            .bold {
                font-weight: bold;
            }
        `,
    },
    rightGridItem: {
        width: 6,
        css: css`
            justify-content: flex-end;
            ${TypographyStyle} {
                font-size: 16px;
                line-height: 19px;
            }
            .bold {
                font-weight: bold;
            }
        `,
    },
    proceedButton: {
        css: css`
            width: 100%;
        `,
    },
    invoiceNumber: {
        color: "primary.main",
    },
    gridContainer: {
        css: css`
            margin-bottom: 6px;
        `,
    },
    listWrapper: {
        css: css`
            max-height: 225px;
            overflow-y: auto;
            overflow-x: hidden;

            &.padding_right {
                padding-right: 5px;
            }

            ::-webkit-scrollbar {
                width: 5px;
                border-radius: 10px;
            }

            ::-webkit-scrollbar-track {
                background: ${color("common.ui2")};
                border-radius: 10px;
            }

            ::-webkit-scrollbar-thumb {
                background: ${color("common.ui1")};
                border-radius: 10px;
            }
        `,
    },
    actionWrapper: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    errorMessage: {
        css: css`
            margin-top: 20px;
        `,
    },
    spinner: {
        color: "primary.main",
        size: 30,
    },
    invoiceItemNumberNonCredit: {
        size: 14,
        lineHeight: "17px",
        color: "primary.main",
    },
};

const PaymentCenterSummary: React.FC<Props> = ({
    selectedInvoicePayments,
    paymentOnAccountData,
    currentStep,
    setCurrentStep,
    selectedCredits,
}) => {
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [isNextStepButtonClicked, setIsNextStepButtonClicked] = useState(false);

    const renderInvoices = () => (
        <>
            {selectedInvoicePayments?.map((invoice, index) => {
                return (
                    <GridContainer key={index} gap={10} {...styles.gridContainer}>
                        <GridItem {...styles.leftGridItem}>
                            <Typography>{translate("Invoice")}</Typography>
                            <PDFLinkCell invoice={invoice} />
                        </GridItem>
                        <GridItem {...styles.rightGridItem}>
                            <Typography>
                                {formatter.format((invoice.amountToPay as any) || invoice.invoiceAmount)}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                );
            })}
        </>
    );

    const renderCredits = () => (
        <>
            {selectedCredits?.map((invoice, index) => {
                return (
                    <GridContainer key={index} gap={10} {...styles.gridContainer}>
                        <GridItem {...styles.leftGridItem}>
                            <Typography>{translate("Credit")}</Typography>
                            <PDFLinkCell invoice={invoice} />
                        </GridItem>
                        <GridItem {...styles.rightGridItem}>
                            <Typography {...styles.invoiceItemNumberDataCellLink}>
                                {formatter.format(invoice.invoiceAmount)}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                );
            })}
        </>
    );

    const renderPaymentOnAccountData = () => (
        <GridContainer gap={10} {...styles.gridContainer}>
            <GridItem {...styles.leftGridItem}>
                <Typography>{translate("Payment On Account")}</Typography>
            </GridItem>
            <GridItem {...styles.rightGridItem}>
                <Typography>{formatter.format(paymentOnAccountData?.value as any)}</Typography>
            </GridItem>
        </GridContainer>
    );

    const isAllInvoicesDataReady = (forceShowErrorMessage = false) => {
        const res = selectedInvoicePayments?.some(invoice => {
            return (
                invoice.shouldShowOtherActions === true &&
                (!invoice.reasonForReducedPrice || !invoice.additionalComments)
            );
        });

        if (res && (isNextStepButtonClicked || forceShowErrorMessage)) {
            setShowErrorMessage(true);
        } else {
            setShowErrorMessage(false);
        }

        return !res;
    };

    let invoicesTotal = calcSelectedInvoicesTotal(selectedInvoicePayments || []);
    if (paymentOnAccountData?.value) {
        invoicesTotal += parseFloat(paymentOnAccountData?.value);
    }
    const creditsTotal = calcSelectedInvoicesTotal(selectedCredits || []);
    const total = invoicesTotal + creditsTotal;

    const goToNextStep = () => {
        setIsNextStepButtonClicked(true);
        if (currentStep === 1) {
            if (isAllInvoicesDataReady(true)) {
                setCurrentStep(2);
            }
        } else if (currentStep === 2) {
            setCurrentStep(3);
        } else if (currentStep === 3) {
            const submitButton = document.getElementById("invoicePaymentSubmitForm-submit");
            submitButton?.click();
        }
    };

    useEffect(() => {
        isAllInvoicesDataReady();
    }, [selectedInvoicePayments]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography variant="h3" {...styles.summaryLabel}>
                {translate("Summary")}
            </Typography>

            <StyledWrapper {...styles.listWrapper} className="padding_right">
                {renderInvoices()}
                {paymentOnAccountData?.value && renderPaymentOnAccountData()}
                {renderCredits()}
            </StyledWrapper>

            <StyledWrapper {...styles.totalWrapper}>
                <GridContainer gap={10}>
                    <GridItem {...styles.leftGridItem}>
                        <Typography className="bold">{translate("Total")}</Typography>
                    </GridItem>
                    <GridItem {...styles.rightGridItem}>
                        <Typography className="bold">{formatter.format(total)}</Typography>
                    </GridItem>
                    <GridItem {...styles.actionWrapper}>
                        <Button
                            {...styles.proceedButton}
                            onClick={goToNextStep}
                            disabled={selectedInvoicePayments?.length === 0}
                        >
                            {currentStep === 3 ? translate("Submit Payment") : translate("Proceed to Next Step")}
                        </Button>

                        {showErrorMessage && (
                            <Notify
                                {...styles.errorMessage}
                                messageType="danger"
                                body={siteMessage("Custom_Payment_Center_Cannot_Proceed")}
                            />
                        )}
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(null, mapDispatchToProps)(PaymentCenterSummary);
